import { useLocation, Link } from "wouter";
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge-base";
import { ChevronDown, ChevronRight, SquareArrowOutUpRight } from "lucide-react";
import { useUIContext } from "@/contexts/UIContext";
import { useTranslation } from "@/hooks/useTranslation";
import { DynamicIcon } from "@/components/DynamicIcon";
import { MenuRowLabel } from "@/components/MenuChip";
import { useNavCounts } from "@/hooks/useNavCounts";
import { SidebarFooter } from "@/components/SidebarFooter";
import { LogoDisc } from "@/components/ui/logo-plate";
import { ScrollDownHint } from "@/components/ui/scroll-down-hint";
import { SwitchProjectButton } from "@/components/SwitchProjectButton";
import { ProjectRowBones } from "@/components/SidebarRowBones";
import { useCompanyScope, useScopedCompany } from "@/lib/companyScope";
import { useQuery } from "@tanstack/react-query";
import { menuVisibilityQuery, projectFeaturesQuery, projectMenuVisibilityQuery } from "@/lib/shellQueries";
import { useTheme } from "@/components/ThemeProvider";
import { useAuth } from "@/hooks/useAuth";
import { normalizeLocation } from "@/lib/utils";
import { formatNumber } from "@/lib/format";
import { ADMIN_MENU_ITEMS, COMPANY_MENU_ITEMS, PROJECT_MENU_ITEMS, foldTabGroups, navChildLabel, tabGroupOf, type NavMenuItem } from "@shared/navigationMenu";
import { useIsHighRise } from "@/hooks/useBuildingTerm";
import { isProjectMenuOpen, type MenuGateInputs } from "@shared/menuGate";
import saleFishDarkLogo from "@/assets/logos/salefish-wordmark-on-light.png";
import saleFishLightLogo from "@/assets/logos/salefish-wordmark-on-dark.png";
import { canAccessCompanySettings, canAccessPlatformSettings } from "@/lib/platformAccess";
import { companySectionFor, isCompanyPath } from "@/lib/platformLayer";
import { preloadCompanySettings, preloadPlatformSettings, whenIdle } from "@/lib/settingsPreload";
import { preloadProjectPage } from "@/lib/projectPagePreload";
import { chipToShow, useSeenMenuChips } from "@/lib/menuChipSeen";
import { fetchJson } from "@/lib/queryClient";
import appIcon from "@/assets/app-icon-128.webp";
import { ProjectSpaces } from "@/components/shell/ProjectSpaces";
import { isFlatShell } from "@/lib/shellFlat";

/* PropPay's menu rows, on the deep panel. Squared to SaleFish's 10px rather
   than PropPay's pill (the CTA radius). 40px for a mouse, 44px wherever a finger may be used
   (any coarse pointer: an iPad with a trackpad still has a touchscreen). */
const NAV_ROW =
  "flex w-full min-h-[40px] [@media(max-height:720px)_and_(not_(any-pointer:coarse))]:min-h-[34px] coarse:min-h-[44px] items-center gap-3 rounded-full px-3 text-left text-body transition-colors duration-instant ease-standard outline-hidden focus-visible:ring-2 focus-visible:ring-sidebar-on-active";
const NAV_IDLE = "text-sidebar-foreground/80 hover:bg-[hsl(var(--menu-wash)/0.05)] hover:text-sidebar-bright";
const NAV_ACTIVE =
  "[background-image:var(--menu-active-bg)] [box-shadow:var(--menu-active-shadow)] type-selected text-[hsl(var(--menu-active-fg))] [&_svg]:text-sidebar-on-active";
const SECTION_LABEL = "px-3 pb-1.5 text-micro font-bold uppercase tracking-[.09em] text-sidebar-dim";

/* A section's heading (Project, Company, Platform) that folds its rows away.
   The reader's choice, kept per device beside the menu groups; open until
   folded. A folded Project keeps the page the reader is on in view, so the
   menu never hides where they are. */
function SectionHeading({ label, open, onToggle, className }: { label: string; open: boolean; onToggle: () => void; className: string }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      className={`${SECTION_LABEL} hit-target group flex w-full items-center gap-1.5 text-left outline-hidden transition-colors duration-instant ease-standard hover:text-sidebar-strong focus-visible:text-sidebar-strong ${className}`}
      data-testid={`section-toggle-${label.toLowerCase()}`}
    >
      <span className="min-w-0 flex-1 truncate">{label}</span>
      <ChevronDown
        className={`size-3.5 shrink-0 opacity-0 transition-[transform,opacity] duration-instant ease-standard group-hover:opacity-80 group-focus-visible:opacity-80 coarse:opacity-60 ${open ? "" : "-rotate-90 opacity-70"}`}
        aria-hidden="true"
      />
    </button>
  );
}

/* The menu groups the reader has opened, per device. A convenience: an empty
   or unreadable store just means every group starts closed. */
const OPEN_GROUPS_KEY = "salefish:nav-open-groups:v1";
function readOpenNavGroups(): Record<string, boolean> {
  try {
    const raw = window.localStorage.getItem(OPEN_GROUPS_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}
function writeOpenNavGroups(groups: Record<string, boolean>) {
  try {
    window.localStorage.setItem(OPEN_GROUPS_KEY, JSON.stringify(groups));
  } catch {
    /* private mode or storage full: the group still opens, it just is not remembered */
  }
}

/* Always open from 768px and absent below it (AppShell), as in PropPay — so
   there is nothing here to close, pin or unpin.

   `variant="sheet"` is the phone's menu (the tab bar's More): PropPay's
   MenuSheet is "the desktop sidebar, raised from the bottom edge", part for
   part, so the two read as one menu on two screens. There the project's
   logo and name head the list, every page is listed, the sheet scrolls the
   whole thing, and `onNavigate` closes it after a row is chosen. */
/**
 * What is waiting behind a menu row: a quiet circle against the name (Andrew,
 * 2026-10-26), not the red notification disc (2026-10-10, "too offensive").
 * The menu says how much is there; the bell is the one red thing that
 * summons. Recoloured for the deep panel only.
 */
function NavCount({ count, noun }: { count: number; noun: string }) {
  const t = useTranslation();
  return (
    <Badge
      variant="secondary"
      className="h-4 min-w-4 shrink-0 justify-center rounded-full bg-[hsl(var(--menu-wash)/0.12)] px-[3px] py-0 leading-none tabular-nums text-sidebar-strong"
      aria-label={t("uiBadgeCount", "{count} {noun}", { count, noun: count === 1 ? noun : `${noun}s` })}
    >
      {formatNumber(count)}
    </Badge>
  );
}

/* The docked menu's list wears the More sheet's "more below" hint; the
   sheet's own body already has one around it (BottomNav). */
function ListFrame({ sheet, children }: { sheet: boolean; children: React.ReactElement }) {
  return sheet ? children : <ScrollDownHint>{children}</ScrollDownHint>;
}

export function ModeAwareSidebar({
  variant = "panel",
  onNavigate,
}: {
  variant?: "panel" | "sheet";
  onNavigate?: () => void;
} = {}) {
  const sheet = variant === "sheet";
  const flat = isFlatShell();
  const [location, navigateTo] = useLocation();
  const setLocation = (to: string) => {
    navigateTo(to);
    onNavigate?.();
  };
  const { theme } = useTheme();
  const t = useTranslation();
  const { mode: contextMode, projectName, projectId } = useUIContext();
  /* On a phone, Company Settings is a page (from the sidenav breakpoint up it
     is CompanyModal, and this sidebar sees the page underneath). The phone's
     sheet no longer lists the company's sections there (it is the off-project
     sheet below, with Company Settings lit); `mode` still follows the path so
     the company's logo and name are fetched for the head where it is drawn. */
  const inCompanySettings = isCompanyPath(location.split('?')[0]);
  const mode = inCompanySettings ? 'company-admin' : contextMode;
  const { user } = useAuth();

  // Fetch project data to get the project logo (only in project mode)
  const { data: projectData } = useQuery<any>({
    queryKey: ['/api/projects', projectId],
    queryFn: async () => {
      const res = await fetch(`/api/projects/${projectId}`, { credentials: 'include' });
      if (!res.ok) return null;
      return res.json();
    },
    enabled: mode === 'project' && !!projectId,
    staleTime: 5 * 60 * 1000,
  });

  // Fetch company data to get the company logo (only in company-admin mode)
  // The company Company Settings is showing — a SaleFish Admin's switcher may
  // have moved it off their own (lib/companyScope.ts).
  const companyScope = useCompanyScope();
  const { data: companyData } = useScopedCompany(companyScope, mode === 'company-admin');

  // Resolve which logo to show in the sidebar
  const sidebarLogoUrl: string | null =
    mode === 'project' && projectData?.projectLogo
      ? projectData.projectLogo
      : mode === 'company-admin' && companyData?.logoUrl
      ? companyData.logoUrl
      : null;
  
  const isDark = theme === 'dark' || (theme === 'system' && typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches);
  
  // Fetch menu visibility settings
  // One definition per shell endpoint (lib/shellQueries.ts) — the sidebar's
  // own `staleTime: 0` copies refetched these on every mount and focus.
  const { data: menuVisibilityData, refetch: refetchMenuVisibility, isFetched: menuVisibilityFetched, failureCount: menuVisibilityFailures } = useQuery<any[]>(menuVisibilityQuery());

  // Fetch per-project menu visibility
  const { data: projectMenuVisibilityData, refetch: refetchProjectMenuVisibility, isFetched: projectMenuVisibilityFetched, failureCount: projectMenuVisibilityFailures } = useQuery<Record<string, boolean> | null>(
    projectMenuVisibilityQuery(projectId, mode === 'project'),
  );
  const projectMenuVisibility: Record<string, boolean> = projectMenuVisibilityData || {};

  // Listen for menu visibility changes to refetch data
  useEffect(() => {
    const handleMenuVisibilityChanged = () => { refetchMenuVisibility(); };
    const handleProjectMenuVisibilityChanged = () => { refetchProjectMenuVisibility(); };

    window.addEventListener("menuVisibilityChanged", handleMenuVisibilityChanged);
    window.addEventListener("projectMenuVisibilityChanged", handleProjectMenuVisibilityChanged);

    return () => {
      window.removeEventListener("menuVisibilityChanged", handleMenuVisibilityChanged);
      window.removeEventListener("projectMenuVisibilityChanged", handleProjectMenuVisibilityChanged);
    };
  }, [refetchMenuVisibility, refetchProjectMenuVisibility]);
  
  // Fetch project features to check subscription status
  const { data: projectFeaturesData, isFetched: projectFeaturesFetched, failureCount: projectFeaturesFailures } = useQuery(projectFeaturesQuery(projectId));
  
  // Email Marketing is on when the project's company holds it (a company plan
  // line since 2026-10-20).
  const hasEmailMarketingSubscription = projectFeaturesData?.emailMarketing === true;
  
  // Navigation comes from the shared registry (`shared/navigationMenu.ts`), not
  // from a list kept here — a menu item declared only in this file has no
  // toggle on Platform Settings → Features, which is how `usage` and
  // `competitorAnalysis` shipped un-hideable.
  const isProjectAdmin = canAccessCompanySettings(user); // company admins and SaleFish Admins
  // Siting is lowrise-only, and Availability's unit page is "Lots" on low-rise
  // and "Suites" on high-rise: one rule decides both (hooks/useBuildingTerm.ts).
  const projectIsHighRise = useIsHighRise();
  const isHighRise = mode === "project" && projectIsHighRise;
  const toNavItem = (item: NavMenuItem) => ({
    title: item.labelKey ? t(item.labelKey, item.label) : item.label,
    url: item.url,
    icon: item.icon,
    key: item.key,
    ...(item.children
      ? {
          children: item.children
            // A project admin's link (Availability › Setup) is not drawn for anyone else.
            .filter((child) => !child.adminOnly || isProjectAdmin)
            .filter((child) => child.requires !== "pricing" || user?.canEditPricing === true)
            .filter((child) => !child.lowriseOnly || !isHighRise)
            .map((child) => {
              const label = navChildLabel(child, isHighRise);
              return { ...child, title: label.titleKey ? t(label.titleKey, label.title) : label.title };
            }),
        }
      : {}),
  });

  // SaleFish Admin navigation items
  const globalItems = ADMIN_MENU_ITEMS.filter((item) => !item.superAdminOnly || user?.role === "SUPER_ADMIN").map(toNavItem);

  // Project navigation items
  const projectItems = PROJECT_MENU_ITEMS.map(toNavItem);

  // Check if user is SaleFish Admin (has admin permissions)
  const isAdmin = canAccessPlatformSettings(user);
  const canOpenCompanySettings = canAccessCompanySettings(user);
  
  // Company Admin navigation items
  const companyAdminItems = COMPANY_MENU_ITEMS.map(toNavItem);
  
  // Non-admin users should only see project items, never admin context
  const allItems = mode === 'global' && isAdmin ? globalItems : mode === 'company-admin' ? companyAdminItems : projectItems;
  const modeLabel = mode === 'global' && isAdmin ? (t("platformSettings", "Platform Settings") || 'Platform Settings') : mode === 'company-admin' ? t("companySettings", "Company Settings") : projectName || t("project", "Project");
  
  // Determine if we should hide navigation (only when in project context without a project selected)
  // Normalize location to handle trailing slashes, query strings, and hash fragments
  const normalizedLoc = normalizeLocation(location);
  // Check if we're on project selection page (no project selected yet)
  const isOnProjectSelection = normalizedLoc === '/projects' || normalizedLoc === '/home';
  // Only hide navigation when in project mode and no project is selected
  // SaleFish Admin context should always show navigation
  // The project picker and the staff home belong to no area: no area list,
  // whatever mode was left over from the last page (a visit to Platform
  // Settings left 'global' behind and /projects listed its sections).
  const shouldHideNavigation = isOnProjectSelection;
  const inPlatformSettings = normalizedLoc.startsWith('/admin');
  const showCompanyGroup = canOpenCompanySettings && mode !== 'company-admin';
  
  const projectGate: MenuGateInputs = {
    globalVisibility: menuVisibilityData,
    projectVisibility: projectMenuVisibility,
    rolePermissions: user?.menuPermissions,
    role: user?.role,
    hasEmailMarketing: hasEmailMarketingSubscription,
    hasShowroom: projectFeaturesData?.showroom === true,
    hasAgentPortal: projectFeaturesData?.agentPortal === true,
    // The same two the phone's tab bar passes (useProjectNav): without them a
    // signing officer or company admin never saw Signing Queue on a desk.
    isCompanyAdmin: user?.isCompanyAdmin === true,
    isSigningOfficer: projectFeaturesData?.signingOfficer === true,
  };

  // Filter and enhance items with visibility and chip data
  const visibleItems = allItems.filter(item => {
    // For SaleFish Admin context (global mode), skip role-based filtering
    // Admin items should always be visible for admin users
    if ((mode === 'global' && isAdmin) || mode === 'company-admin') {
      // Only check global visibility for admin items (default to visible if no setting)
      const setting = menuVisibilityData?.find(s => s.menuKey === item.key);
      return !setting || setting.visible === true;
    }
    
    // A project's menus answer to the one shared gate (shared/menuGate.ts),
    // the same one the phone's bottom bar and the server's routes use: a copy
    // kept here missed the Agent Portal's rules (2026-10-22).
    return isProjectMenuOpen(projectGate, item.key);
  });
  // Sales' four tabs (Offers, Sales, Signing, Deposits) are one row here,
  // lit on any of them; each tab still answers to its own gate above.
  const rows = mode === 'project'
    ? foldTabGroups(visibleItems, tabGroupOf, (group, tabs) => ({
        title: t(group.labelKey, group.label),
        url: tabs[0].url,
        icon: group.icon,
        key: group.key,
        tabUrls: tabs.map((tab) => tab.url),
      }))
    : visibleItems;
  
  // Build chips map for visible items
  // New / Updated only until the reader has opened the item (lib/menuChipSeen).
  const seenChips = useSeenMenuChips();
  const menuChips: Record<string, string | null> = {};
  if (menuVisibilityData) {
    menuVisibilityData.forEach((setting: any) => {
      if (setting.menuKey) {
        menuChips[setting.menuKey] = chipToShow(setting.menuKey, setting, seenChips);
      }
    });
  }
  
  const navCounts = useNavCounts();
  /* Which groups (Availability) the reader has opened. The READER's choice,
     kept across pages and launches — never the route's. A group that opened
     itself on its own pages pushed every row below it (Reports, Agent Portal,
     Showroom, Project Settings, Switch Project) 276px down on Availability
     and back up on the next page (shift-audit chrome-nav, 2026-10-28); the
     chrome holds still from page to page. Closed until opened. */
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(readOpenNavGroups);
  const setGroupOpen = (key: string, open: boolean) => {
    setOpenGroups((prev) => {
      const next = { ...prev, [key]: open };
      writeOpenNavGroups(next);
      return next;
    });
  };
  /* Every section starts open, except that a SaleFish Admin (the Platform
     Settings rule, lib/platformAccess) starts with Company and Platform
     folded: they live in a project's menu, not in those two. Once the reader
     opens or folds one, their choice stands. */
  const foldedByDefault = (key: string) => isAdmin && (key === 'company' || key === 'platform');
  const sectionOpen = (key: string) => {
    const chosen = openGroups[`section:${key}`];
    return chosen === undefined ? !foldedByDefault(key) : chosen;
  };
  const toggleSection = (key: string) => setGroupOpen(`section:${key}`, !sectionOpen(key));
  /* A project page's chunk is fetched on the press (and on hover with a
     mouse, and on keyboard focus), so the route usually renders on the tap's
     own commit instead of behind its loading fallback. Project rows only:
     the settings areas warm themselves (preloadPlatformSettings, below). */
  const warmOn = (url: string) => {
    if (mode !== 'project' || !url) return {};
    const warm = () => preloadProjectPage(url);
    return { onPointerDown: warm, onPointerEnter: warm, onFocus: warm };
  };
  /* The row the reader is on (or one of its tabs or sub-pages): what a folded
     section still shows. */
  const isOnRow = (item: any) => {
    const urls: string[] = [item.url, ...(item.tabUrls ?? []), ...((item.children ?? []).map((c: any) => c.url))];
    const pid = location.split('/')[2];
    return urls.some((u) => mode === 'project'
      ? location.startsWith(`/projects/${pid}/${u}`)
      : location === u || location.startsWith(`${u}/`));
  };
  const renderNavItem = (item: any) => {
    const chip = menuChips[item.key];
    const isComingSoon = chip === 'coming_soon';
    const currentProjectId = location.split('/')[2];
    // A company section keeps `?projectId=` — it says which company.
    const companyQuery = mode === 'company-admin' && typeof window !== 'undefined' ? window.location.search : '';
    const itemFullUrl = mode === 'project' ? `/projects/${currentProjectId}/${item.url}` : `${item.url}${companyQuery}`;
    const isActive = mode === 'global'
      ? location === item.url || location.startsWith(item.url + '/')
      : mode === 'company-admin'
        ? !!companySectionFor(location, [{ href: item.url }])
        // A page with its own tabs at sub-paths (Showroom's /showroom/queue…)
        // stays lit on every tab, as the tab bar already reads it.
        : ((item.tabUrls as string[] | undefined) ?? [item.url]).some((u) => {
            const full = `/projects/${currentProjectId}/${u}`;
            return location === full || location.startsWith(full + '/');
          });

    // The name, what is waiting there, and its chip (components/MenuChip): the
    // chip never cuts the name. A row that is not open (Coming Soon) has no count.
    const waiting = isComingSoon || item.children ? undefined : navCounts[item.key];
    const label = (
      <MenuRowLabel
        // On a phone (the More sheet) Website analytics is just "Website" (Andrew, 2026-11-02).
        title={sheet && item.key === 'websiteAnalytics' ? t('navWebsiteShort', 'Website') : item.title}
        chip={chip}
        count={waiting && waiting.count > 0 ? <NavCount count={waiting.count} noun={waiting.noun} /> : undefined}
      />
    );

    if (isComingSoon) {
      return (
        <div key={item.url} aria-disabled="true" className={`${NAV_ROW} cursor-not-allowed text-sidebar-foreground/50`}>
          <DynamicIcon name={item.icon} className="size-[18px] shrink-0 opacity-80" />
          {label}
        </div>
      );
    }

    // Items with children: a disclosure the reader opens and closes. It
    // stays as they left it on every page (see openGroups above); the row is
    // lit while any of its pages is open, whether or not it is expanded.
    if (item.children) {
      const inGroup = mode === 'project'
        ? location.startsWith(`/projects/${currentProjectId}/${item.url}`)
        : location.startsWith(item.url);
      const isExpanded = openGroups[item.key] === true;

      const handleParentClick = () => {
        setGroupOpen(item.key, !isExpanded);
        // Opening it from elsewhere also goes there, as the row always did;
        // inside the area it only shows or hides the pages.
        if (!isExpanded && !inGroup) {
          const defaultChild = item.children[0];
          navigateTo(mode === 'project'
            ? `/projects/${currentProjectId}/${defaultChild.url}`
            : defaultChild.url);
        }
      };

      return (
        <div key={item.url}>
          <button
            type="button"
            onClick={handleParentClick}
            {...warmOn(item.children[0]?.url ?? item.url)}
            aria-expanded={isExpanded}
            aria-current={inGroup && !isExpanded ? 'page' : undefined}
            className={`${NAV_ROW} ${inGroup ? (isExpanded ? 'type-selected text-sidebar-strong' : NAV_ACTIVE) : NAV_IDLE}`}
          >
            <DynamicIcon name={item.icon} className={`size-[18px] shrink-0 opacity-95 ${inGroup ? 'text-sidebar-on-active' : ''}`} />
            {label}
            {isExpanded
              ? <ChevronDown className="size-3.5 shrink-0 opacity-70" />
              : <ChevronRight className="size-3.5 shrink-0 opacity-70" />}
          </button>
          {isExpanded && (
            <div className="ml-[21px] mt-0.5 flex flex-col gap-0.5 border-l border-sidebar-border pl-2.5">
              {item.children.map((child: any) => {
                const childFullUrl = mode === 'project'
                  ? `/projects/${currentProjectId}/${child.url}`
                  : child.url;
                const isChildActive = location.startsWith(childFullUrl);
                return (
                  <button
                    type="button"
                    key={child.url}
                    onClick={() => setLocation(childFullUrl)}
                    {...warmOn(child.url)}
                    aria-current={isChildActive ? 'page' : undefined}
                    className={`${NAV_ROW} px-2.5 ${isChildActive ? NAV_ACTIVE : NAV_IDLE}`}
                  >
                    <span className="min-w-0 break-words py-1.5 leading-snug">{child.title}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      );
    }

    return (
      <button
        type="button"
        key={item.url}
        onClick={() => setLocation(itemFullUrl)}
        {...warmOn(item.url)}
        aria-current={isActive ? 'page' : undefined}
        className={`${NAV_ROW} ${isActive ? NAV_ACTIVE : NAV_IDLE}`}
      >
        <DynamicIcon name={item.icon} className="size-[18px] shrink-0 opacity-95" />
        {/* The count is part of the row's name for a screen reader:
            "Tasks, 3 open tasks" (D10). */}
        {label}
      </button>
    );
  };

  // The head of the menu: the project (or company) as PropPay shows it — its
  // mark in a light disc, the name beneath. No chip and no rule above it
  // (Andrew, 2026-09-27). A logo is drawn for a light page, so it sits on its
  // own white disc rather than straight on the deep panel.
  /* Off a project (the project list), the head is WHO the reader is here
     for: their own company, as the project head is the project (Andrew,
     2026-10-03: "the header of the menu should show the company details of
     the company the user logged in as"). SaleFish staff are SaleFish, drawn
     with SaleFish's own mark — whatever company row their account points at.
     Same key and fetcher as Account Settings, so it is one cached read. */
  const isStaff = canAccessPlatformSettings(user); // SaleFish Admins — lib/platformAccess
  const companyHead = shouldHideNavigation && !sheet;
  const { data: ownCompany } = useQuery<any>({
    queryKey: ['/api/companies', user?.companyId],
    queryFn: ({ signal }) =>
      user?.companyId ? fetchJson(`/api/companies/${user.companyId}`, { signal }) : Promise.resolve(null),
    enabled: companyHead && !isStaff && !!user?.companyId,
    staleTime: 5 * 60 * 1000,
  });
  const ownCompanyName: string | null = companyHead
    ? (isStaff ? 'SaleFish' : (ownCompany?.companyName || ownCompany?.name || null))
    : null;

  const headName = mode === 'project' ? projectName : mode === 'company-admin' ? (companyData?.companyName || companyData?.name) : ownCompanyName;
  /* The head's rows are held from the first frame in a project or the company
     area, name or not. It used to mount only once the name was known, and
     arriving it pushed the whole menu down by its own height — 148px on an
     iPad with a long project name — on a cold load of any project route. */
  const showHead = companyHead
    ? (isStaff || !!user?.companyId)
    : !shouldHideNavigation && (!!headName || (mode === 'project' && !!projectId) || mode === 'company-admin');

  /* The rows are drawn once, when the three lists that decide them are in.
     Before, they were drawn straight away from the unfiltered registry and
     then corrected: Email Marketing arrived with the features query and
     pushed every row under it down 48px, and a row hidden by the menu or
     project visibility settings flashed and then vanished. So the whole list
     (and the Company and Platform groups under it, which the rows would push)
     appears at once: rows appearing in an empty panel move nothing; rows
     moving under the reader's finger do.
     A list counts as in once it has answered OR failed once — the first
     failure releases the rows rather than holding the menu empty through the
     retry backoff — and only the queries enabled in this mode are waited on
     (a disabled query never fetches). Once cached, as on every later visit
     in the session, this is true on the first render. */
  const rowsReady =
    (menuVisibilityFetched || menuVisibilityFailures > 0) &&
    (!projectId || projectFeaturesFetched || projectFeaturesFailures > 0) &&
    (mode !== 'project' || !projectId || projectMenuVisibilityFetched || projectMenuVisibilityFailures > 0);

  /* The two settings layers are lazy, and opening one was a chain of round
     trips — the modal's chunk, then its first section's — with nothing on
     screen for the first. Fetch both once the page is idle, for the people
     who have the rows, and again on hover / touch-down / focus of the row in
     case idle has not come yet. */
  const offerPlatform = rowsReady && isAdmin && !inPlatformSettings;
  const offerCompany = rowsReady && showCompanyGroup;
  useEffect(() => {
    if (sheet || (!offerPlatform && !offerCompany)) return;
    return whenIdle(() => {
      if (offerPlatform) preloadPlatformSettings();
      if (offerCompany) preloadCompanySettings();
    });
  }, [sheet, offerPlatform, offerCompany]);

  /* The two rows out of here — Company Settings and Platform Settings — drawn
     once, for the panel's eyebrowed groups below and for the phone's
     off-project sheet. Lit when the reader is in that area: only the sheet
     can be open there (on a phone both areas are pages), and the row then
     says where they are. */
  const companyRow = (
    <button
      type="button"
      onClick={() => {
        const dest = mode === 'project' && projectId
          ? `/company-admin?projectId=${projectId}`
          : '/company-admin';
        setLocation(dest);
      }}
      onPointerEnter={preloadCompanySettings}
      onPointerDown={preloadCompanySettings}
      onFocus={preloadCompanySettings}
      aria-current={inCompanySettings ? 'page' : undefined}
      className={`${NAV_ROW} ${inCompanySettings ? NAV_ACTIVE : NAV_IDLE}`}
      data-testid="button-context-company-admin"
    >
      <DynamicIcon name="BriefcaseBusiness" className="size-[18px] shrink-0 opacity-95" />
      <span className="min-w-0 flex-1 break-words py-1.5 leading-snug">{t("companySettings", "Company Settings")}</span>
      {/* PropPay's marker, as on Platform Settings: this row opens a
          layer over the page (from the sidenav breakpoint up). */}
      <SquareArrowOutUpRight className="size-[15px] shrink-0 opacity-60" aria-hidden="true" />
    </button>
  );
  const platformRow = (
    <button
      type="button"
      onClick={() => setLocation('/admin')}
      onPointerEnter={preloadPlatformSettings}
      onPointerDown={preloadPlatformSettings}
      onFocus={preloadPlatformSettings}
      aria-current={inPlatformSettings ? 'page' : undefined}
      className={`${NAV_ROW} ${inPlatformSettings ? NAV_ACTIVE : NAV_IDLE}`}
      data-testid="button-context-salefish-admin"
    >
      <DynamicIcon name="MonitorCog" className="size-[18px] shrink-0 opacity-95" />
      <span className="min-w-0 flex-1 break-words py-1.5 leading-snug">{t("platformSettings", "Platform Settings")}</span>
      {/* PropPay's marker: this row opens a layer over the page. */}
      <SquareArrowOutUpRight className="size-[15px] shrink-0 opacity-60" aria-hidden="true" />
    </button>
  );

  /* THE PHONE'S SHEET OFF A PROJECT (Andrew, 2026-09-29, as PropPay): the
     project list, Platform Settings, Company Settings and the account pages
     have no project to head the menu, so it is the same menu with the
     project's group gone — Switch Project (not on the list itself, which is
     where it leads), Company Settings and Platform Settings, as plain rows
     with no eyebrow. Platform Settings' and Company Settings' own sections
     never come in here: on a phone they are the page's own list, and a menu
     that changed shape to repeat them read as a second, different menu.
     The sheet's ✕ sits in the handle strip above the body, so the rows need
     only a little air (`pt-3`) below it. Read from the location, like the
     tab bar: the context keeps its project through Company Settings. */
  const onProjectPage = contextMode === 'project' && !!projectId && normalizedLoc.startsWith('/projects/');
  if (sheet && !onProjectPage) {
    return (
      <nav aria-label={t("menu", "Menu")} className="relative flex flex-col px-3 pb-2 pt-1">
        <div className="flex flex-col gap-0.5 pt-3 coarse:gap-1">
          {/* Raised, and with more air beneath it than between rows (16px
              with the gap): it leaves, where the rows below move between
              areas. Andrew, 2026-09-29: "put more space between the Switch
              Project and Platform Settings". */}
          {!isOnProjectSelection && <SwitchProjectButton className="mb-3" onClick={() => setLocation('/projects')} />}
          {canOpenCompanySettings && companyRow}
          {isAdmin && platformRow}
        </div>
        <SidebarFooter onNavigate={onNavigate} className="pt-8" />
      </nav>
    );
  }

  /* A <nav> around the WHOLE sidebar, not just its menu list — one landmark,
     labelled "Sidebar" (the phone tab bar is "Main", the breadcrumb is
     "Breadcrumb"); "Menu" when it is the phone's sheet. */
  return (
    <nav aria-label={sheet ? t("menu", "Menu") : t("shellNavSidebarLabel", "Sidebar")} className={`relative flex flex-col px-3 ${sheet ? 'pb-2 pt-1' : 'h-full pb-4 pt-5'}`}>
      {showHead && (
        <div
          data-testid="sidebar-head"
          className="flex shrink-0 flex-col items-center gap-2 pb-2 pt-1.5 menu-short:gap-1.5 menu-short:pb-1 menu-short:pt-0 menu-short:[--letter-avatar-size:46px]"
        >
          {companyHead && isStaff ? (
            // SaleFish's own mark, the disc the project picker's header uses.
            <img src={appIcon} alt="" aria-hidden="true" width={64} height={64} decoding="async" className="size-16 shrink-0 rounded-full shadow-xs menu-short:size-[46px]" />
          ) : (
            // A circle with the card's background rules (LogoDisc, 2026-10-23):
            // 64px, or 46 in a short window, as the disc always was.
            <>
              <LogoDisc name={headName || ""} src={companyHead ? (ownCompany?.logoUrl ?? null) : sidebarLogoUrl} size={64} className="menu-short:hidden" />
              <LogoDisc name={headName || ""} src={companyHead ? (ownCompany?.logoUrl ?? null) : sidebarLogoUrl} size={46} className="hidden menu-short:flex" />
            </>
          )}
          {/* Always two lines tall, never more: the menu below starts at the
              same height for every project and before the project has
              loaded. It reserved one line while loading and took two for a
              long name, so every row dropped 41px when the name arrived
              (shift-audit chrome-load, 2026-10-27) \u2014 and moved again on each
              switch between a short name and a long one. */}
          <div className="flex min-h-[2lh] items-center justify-center px-2 text-center text-body font-bold leading-snug text-sidebar-strong">
            <span className="line-clamp-2">{headName || <span aria-hidden="true">{"\u00a0"}</span>}</span>
          </div>
        </div>
      )}

      {/* The list is the only region that scrolls when the window is too short
          for the whole menu — the foot below stays pinned, so who is signed in
          and the way out are always in view. It says so the way the phone's
          More sheet does (ui/scroll-down-hint: a fade and a chevron while there
          is more): on an iPad held sideways the list is half hidden, with no
          scrollbar, and read as a menu that ended at Sales (2026-11-02). */}
      <ListFrame sheet={sheet}>
      <div className={sheet ? 'flex flex-col' : 'momentum flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain scrollbar-hide'}>
        {/* Until the permission reads answer, the cold-launch skeleton's own
            rows (SidebarRowBones) — never an empty menu between the shell's
            bones and the real rows. */}
        {!rowsReady && !sheet && mode === 'project' && !shouldHideNavigation && (
          <div aria-hidden="true">
            <div className={`${SECTION_LABEL} ${showHead ? 'pt-3.5 menu-short:pt-2' : 'pt-1'}`}>{t("project", "Project") || "Project"}</div>
            <ProjectRowBones />
          </div>
        )}
        {rowsReady && !shouldHideNavigation && (
          <>
            <SectionHeading
              label={mode === 'project' ? (t("project", "Project") || "Project") : mode === 'company-admin' ? (t("companySettings", "Company Settings") || "Company Settings") : (t("platformSettings", "Platform Settings") || "Platform Settings")}
              open={sectionOpen('area')}
              onToggle={() => toggleSection('area')}
              className={showHead ? 'pt-3.5 menu-short:pt-2' : 'pt-1'}
            />
            <div className="flex flex-col gap-0.5 coarse:gap-1">
              {(sectionOpen('area') ? rows : rows.filter(isOnRow)).map(renderNavItem)}
              {/* Switch Project closes the area's own list — under Project
                  Settings in a project — rather than heading Platform
                  (Andrew, 2026-09-28). Never on the project list itself,
                  which is where it leads (that page hides this list). */}
              {/* A clear break on both sides: it leaves the area, where the
                  rows above move within it (Andrew, 2026-09-29: "always a
                  bit more space around the Switch Project menu item"). */}
              {!(flat && !sheet) && <SwitchProjectButton className="mb-3 mt-4" onClick={() => setLocation('/projects')} />}
            </div>
          </>
        )}

        {/* The project list has no project rows (shouldHideNavigation), so
            Company and Platform are the menu: they sit at the top, under the
            reader's company (Andrew, 2026-10-03 — they were briefly seated on
            the footer over an empty panel). */}
        {/* Company — company admins and super admins, outside the company area */}
        {rowsReady && showCompanyGroup && (
          <>
            <SectionHeading
              label={t("company", "Company") || "Company"}
              open={sectionOpen('company')}
              onToggle={() => toggleSection('company')}
              className={shouldHideNavigation ? (showHead ? 'pt-3.5 menu-short:pt-2' : 'pt-1') : 'pt-7 menu-short:pt-3'}
            />
            {sectionOpen('company') && companyRow}
          </>
        )}

        {/* Platform — the way into Platform Settings. A wider break than
            between rows: it is a different kind of place, and the gap says so
            before the label is read. Gone where it would head nothing. */}
        {rowsReady && isAdmin && !inPlatformSettings && (
          <>
            <SectionHeading
              label={t("platform", "Platform")}
              open={sectionOpen('platform')}
              onToggle={() => toggleSection('platform')}
              className={!shouldHideNavigation || showCompanyGroup ? 'pt-7 menu-short:pt-3' : 'pt-1'}
            />
            {sectionOpen('platform') && (
              <div className="flex flex-col gap-0.5 coarse:gap-1">
                {platformRow}
              </div>
            )}
          </>
        )}
      </div>
      </ListFrame>

      {/* Spaces: the reader's projects, a section like the ones above. */}
      {flat && !sheet && (
        <div className="shrink-0 pt-3" data-testid="section-spaces">
          <SectionHeading
            label={t("shellSpaces", "Spaces")}
            open={sectionOpen('spaces')}
            onToggle={() => toggleSection('spaces')}
            className="pt-1"
          />
          {sectionOpen('spaces') && <ProjectSpaces currentProjectId={mode === 'project' ? projectId ?? null : null} area={isCompanyPath(location) ? 'company' : /^\/admin(\/|$)/.test(location) ? 'platform' : 'project'} />}
        </div>
      )}
      <SidebarFooter onNavigate={onNavigate} className={sheet ? 'pt-8' : ''} />
    </nav>
  );
}
